import type { Order } from '../types';
import type { ProductSortKey, ProductSortOption } from './productSort';

/**
 * Sort modes for purchase/sales order lists (client-side).
 * Reuses catalog keys so ProductSortDropdown works; "popular" = status priority then newest.
 */
export const ORDER_SORT_OPTIONS: readonly ProductSortOption[] = [
    {
        key: 'newest',
        apiValue: 'latest',
        labelKey: 'catalog.sort.newest',
        labelFallback: 'Newest first',
    },
    {
        key: 'priceAsc',
        apiValue: 'price-asc',
        labelKey: 'catalog.sort.priceAsc',
        labelFallback: 'Price: Low to High',
    },
    {
        key: 'priceDesc',
        apiValue: 'price-desc',
        labelKey: 'catalog.sort.priceDesc',
        labelFallback: 'Price: High to Low',
    },
    {
        key: 'popular',
        apiValue: 'popular',
        labelKey: 'account.orders.sort.status',
        labelFallback: 'By status',
    },
] as const;

const statusRank = (status: string | undefined): number => {
    const s = String(status ?? '');
    // Active fulfillment first for sales; useful for purchases too
    if (s === 'Paid' || s === 'Processing') return 0;
    if (s === 'Shipped' || s === 'InTransit') return 1;
    if (s === 'Pending') return 2;
    if (s === 'Delivered' || s === 'Completed') return 3;
    if (s === 'Problem') return 4;
    if (s === 'Cancelled' || s === 'Refunded') return 5;
    return 6;
};

const orderDateMs = (o: Order) => (o.orderDate ? Date.parse(o.orderDate) : 0);

export function sortOrdersClient(orders: Order[], key: ProductSortKey): Order[] {
    const copy = [...orders];
    switch (key) {
        case 'priceAsc':
            return copy.sort(
                (a, b) => (a.total ?? 0) - (b.total ?? 0) || orderDateMs(b) - orderDateMs(a)
            );
        case 'priceDesc':
            return copy.sort(
                (a, b) => (b.total ?? 0) - (a.total ?? 0) || orderDateMs(b) - orderDateMs(a)
            );
        case 'popular':
            return copy.sort(
                (a, b) =>
                    statusRank(String(a.status)) - statusRank(String(b.status)) ||
                    orderDateMs(b) - orderDateMs(a)
            );
        case 'newest':
        default:
            return copy.sort(
                (a, b) => orderDateMs(b) - orderDateMs(a) || (b.id ?? 0) - (a.id ?? 0)
            );
    }
}
